import React from "react";
import { connect, useSelector } from "react-redux";
import { format } from "date-fns";
import lodashGet from "lodash/get";

import { BackButton } from "jazasoft";
import PageFooter from "jazasoft/lib/mui/layout/PageFooter";
import ExcelIcon from "mdi-material-ui/FileExcel";
import MuiTable from "../../../components/MuiTable";

import Report from "./Report";
import { useTenant, useMutation, useQuery, useDeepMemo, useUnit, useDeepEffect } from "../../../hooks";
import { findMaxValue, getDistinctValues, humanizeNumber, isEmpty, listFromCsv, toFixed } from "../../../utils/helpers";
import { convertTableToCell } from "../../../utils/excelHelper";

const getSearchText = filterValues => {
  const { unitId, productList, season, customerList } = filterValues;
  let searchItems = [];
  if (!isEmpty(unitId)) {
    searchItems.push(`unitId=='${unitId}'`);
  }
  if (!isEmpty(productList)) {
    searchItems.push(`product=in=(${productList.map(e => `'${e.name}'`).join(",")})`);
  }
  if (!isEmpty(season)) {
    searchItems.push(`season=='${season}'`);
  }
  if (!isEmpty(customerList)) {
    searchItems.push(`customer=in=(${customerList.map(e => `'${e.name}'`).join(",")})`);
  }
  return searchItems.join(";");
};

const AggregateReport = props => {
  const { start, end, unit, productList, season, customerList, unitId } = props?.filter;

  const i18nMessage = useSelector(state => state?.i18n?.messages || {});
  const tenant = useTenant();
  const { allUnitList } = useUnit();

  const [download] = useMutation({ rawResponse: true });

  const fromDate = start ? format(new Date(start), "yyyy-MM-dd") : null;
  const toDate = end ? format(new Date(end), "yyyy-MM-dd") : null;
  const searchText = getSearchText(props.filter || {});

  const { data: rows = [] } = useQuery(
    fromDate && {
      url: "daily-cutting-report",
      params: {
        fromDate,
        ...(toDate && { toDate }),
        ...(!isEmpty(searchText) && { search: searchText })
      }
    }
  );

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "unit", title: "Unit", filterOptions: { filter: true } },
      { dataKey: "poRef", title: "ERP No" },
      { dataKey: "fpo", title: "FPO No" },
      { dataKey: "customer", title: "Customer", filterOptions: { filter: true } },
      { dataKey: "season", title: "Season" },
      { dataKey: "product", title: "Product", filterOptions: { filter: true } },
      { dataKey: "styleNo", title: "Style No.", filterOptions: { filter: true } },
      { dataKey: "style", title: "Style Name", filterOptions: { filter: true } },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true } },
      { dataKey: "part", title: "Part" },
      { dataKey: "lotValue", title: "Lot" },
      { dataKey: "inseams", title: "inseams" },
      { dataKey: "destinations", title: "Destinations" },
      { dataKey: "deliveries", title: "deliveries" },
      { dataKey: "delModes", title: "Delivery Modes" },
      { dataKey: "customerFlowRefs", title: "BPO" },
      { dataKey: "flowRefs", title: "Line ID" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "fpoQty", title: "FPO Qty" },
      { dataKey: "cutQty", title: "Cut Qty", highlight: true },
      { dataKey: "cumCutQty", title: "Cum. Cut Qty", align: "center" },
      {
        dataKey: "cumCutQtyPercent",
        title: "Cum. Cut Qty (%)",
        align: "center",
        render: value => `${toFixed(value * 100, 1)}%`,
        cellStyle: { dataFormat: "0.0%" }
      },
      { dataKey: "state", title: "Status" }
    ];

    if (rows.every(e => isEmpty(e.destinations))) {
      cols = cols.filter(c => c.dataKey !== "destinations");
    }
    if (rows.every(e => isEmpty(e.inseams))) {
      cols = cols.filter(c => c.dataKey !== "inseams");
    }
    if (rows.every(e => isEmpty(e.deliveries))) {
      cols = cols.filter(c => c.dataKey !== "deliveries");
    }
    if (rows.every(e => isEmpty(e.delModes))) {
      cols = cols.filter(c => c.dataKey !== "delModes");
    }
    if (rows.every(e => isEmpty(e.customerFlowRefs))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRefs");
    }
    if (rows.every(e => isEmpty(e.flowRefs))) {
      cols = cols.filter(c => c.dataKey !== "flowRefs");
    }

    return cols;
  }, [rows]);

  const finalRows = useDeepMemo(() => {
    const orderFlowIds = getDistinctValues(rows.map(e => e.orderFlowId));
    const finalRows = orderFlowIds.map(flowId => {
      const flowRows = rows.filter(e => e.orderFlowId === flowId);
      const totalCutQty = flowRows.reduce((acc, e) => acc + e.cutQty, 0);
      const maxCumCutQty = findMaxValue(flowRows.map(e => e.cumCutQty));
      const anyRow = flowRows.sort((a, b) => b.date - a.date)[0];

      return {
        ...anyRow,
        unit: allUnitList?.find(u => u.unitId === anyRow.unitId)?.name || anyRow.unitId,
        inseams: listFromCsv(anyRow.inseams).join(", "),
        destinations: listFromCsv(anyRow.destinations).join(", "),
        deliveries: listFromCsv(anyRow.deliveries).join(", "),
        delModes: listFromCsv(anyRow.delModes).join(", "),
        flowRefs: listFromCsv(anyRow.flowRefs).join(", "),
        customerFlowRefs: listFromCsv(anyRow.customerFlowRefs).join(", "),
        cutQty: totalCutQty,
        cumCutQty: maxCumCutQty,
        cumCutQtyPercent: anyRow?.orderQty > 0 ? toFixed(maxCumCutQty / anyRow?.orderQty, 3) : 0
      };
    });
    return finalRows;
  }, [rows, allUnitList]);

  useDeepEffect(() => {
    if (isEmpty(finalRows)) {
      typeof props.updateTotal === "function" && props.updateTotal(0);
    } else {
      const total = finalRows?.reduce((acc, row) => acc + row?.cutQty, 0);
      typeof props.updateTotal === "function" && props.updateTotal(humanizeNumber(total || 0));
    }
  }, [rows, allUnitList, finalRows]);

  const onExcelDownload = (rows = [], onDownloadComplete) => {
    let finalRows = rows;
    const fileName = `Aggregate Cutting Report.xlsx`.replaceAll(" ", "");
    const columnList = columns.map((c, idx) => ({
      ...c,
      colNo: idx + 1,
      title: lodashGet(i18nMessage, `resources.report.cutting.${c.dataKey}`, c.title)
    }));

    const rowList = finalRows.map((row, idx) => ({
      rowNo: idx + 1,
      cellList: columnList.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] }))
    }));

    const dailyColumnList = [{ dataKey: "date", title: "Date" }, ...columns].map((c, idx) => ({
      ...c,
      colNo: idx + 1,
      title: lodashGet(i18nMessage, `resources.report.cutting.${c.dataKey}`, c.title),
      type: c.dataKey === "date" ? "date" : undefined
    }));

    const dailyRowList = rows
      .sort((a, b) => a.date - b.date)
      .map(row => ({
        ...row,
        unit: allUnitList?.find(u => u.unitId === row.unitId)?.name || row.unitId,
        inseams: listFromCsv(row.inseams).join(", "),
        destinations: listFromCsv(row.destinations).join(", "),
        deliveries: listFromCsv(row.deliveries).join(", "),
        delModes: listFromCsv(row.delModes).join(", "),
        flowRefs: listFromCsv(row.flowRefs).join(", "),
        customerFlowRefs: listFromCsv(row.customerFlowRefs).join(", "),
        cumCutQtyPercent: row?.orderQty > 0 ? toFixed((row.cumCutQty || 0) / row.orderQty, 3) : 0
      }))
      .map((row, idx) => ({
        rowNo: idx + 1,
        cellList: dailyColumnList.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] }))
      }));

    const maxColNo = columnList?.length;
    const dailyMaxColNo = dailyColumnList?.length;

    let filterColumns = [
      { dataKey: "startDate", title: "Start Date" },
      { dataKey: "endDate", title: "End Date" },
      { dataKey: "unit", title: "Unit" },
      { dataKey: "season", title: "Season" },
      { dataKey: "productList", title: "Products" },
      { dataKey: "customerList", title: "Customers" }
    ];

    let filterRows = [
      {
        startDate: start ? format(new Date(start), "dd MMM yy") : "",
        endDate: end ? format(new Date(end), "dd MMM yy") : "",
        unit: unit || "",
        productList: !isEmpty(productList) ? productList.map(e => e.name).join(", ") : "",
        season: season || "",
        customerList: !isEmpty(customerList) ? customerList.map(e => e.name).join(", ") : ""
      }
    ];

    const filterCells = convertTableToCell(filterColumns, filterRows, 3, maxColNo);
    const dailyFilterCells = convertTableToCell(filterColumns, filterRows, 3, dailyMaxColNo);

    const fUnit = allUnitList.find(e => e.unitId === unitId) || {};
    const cellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: fUnit?.displayName || fUnit?.name || tenant?.name || "",
        colEnd: maxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Aggregate Cutting Report",
        colEnd: maxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 32
      },
      ...filterCells
    ];

    const dailyCellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: fUnit?.displayName || fUnit?.name || tenant?.name || "",
        colEnd: dailyMaxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Aggregate Cutting Report",
        colEnd: dailyMaxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 32
      },
      ...dailyFilterCells
    ];

    const sheetNo1 = {
      sheetNo: 0,
      sheetName: "Aggregate Cutting Report",
      excelData: {
        columnList,
        rowList,
        cellList
      }
    };

    const sheetNo2 = {
      sheetNo: 1,
      sheetName: "Daily Cutting Report",
      excelData: {
        columnList: dailyColumnList,
        rowList: dailyRowList,
        cellList: dailyCellList
      }
    };

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      data: [sheetNo1, sheetNo2],
      params: { filename: fileName }
    };

    download(options, {
      onSuccess: response => {
        try {
          if (response.status === 200) {
            const header = response.headers["content-disposition"];
            var filename = header ? header.match(/filename="(.+)"/)[1] : fileName;

            let blob = new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            link.click();
          }
        } finally {
          onDownloadComplete && onDownloadComplete();
        }
      },
      onFailure: () => onDownloadComplete && onDownloadComplete()
    });
  };

  const onFooterActionClick = (event, action, rows, filterValues, onActionComplete) => {
    if (action === "download") {
      const activeFilterKeys = Object.keys(filterValues || {}).filter(key => !isEmpty(filterValues[key]));
      const rowsToDownload = rows.filter(row => activeFilterKeys.every(key => row[key] === filterValues[key]));

      onExcelDownload(rowsToDownload, () => onActionComplete && onActionComplete(rows));
    }
  };

  return (
    <>
      <div style={{ marginTop: 10 }}>
        <MuiTable
          title="Order"
          columns={columns}
          rows={finalRows}
          searchable={true}
          searchKeys={["poRef", "fpo", "style", "unitId", "flowRefs", "customerFlowRefs", "styleNo"]}
          pageable={true}
          cellLength={30}
          i18nKey="resources.report.arr.excelColumns"
          footerActions={[
            {
              name: "download",
              tooltip: "Download",
              icon: <ExcelIcon />,
              options: { style: { color: "green" }, variant: "outlined" }
            }
          ]}
          onFooterActionClick={onFooterActionClick}
        />
      </div>
      <PageFooter style={{ margin: "20px" }}>
        <BackButton variant="contained" color="primary" />
      </PageFooter>
    </>
  );
};

const mapStateToProps = state => ({ i18nMessages: state?.i18n?.messages || {} });
const StyledAggregateReconciliationReport = connect(mapStateToProps)(AggregateReport);

const filterKeys = ["start", "end", "unit", "season", "product", "customer"];
const AggregateCuttingReport = props => {
  return (
    <Report title="Aggregate Cutting Report " filterOnly={true} defaultMonths={1} totalLabel={"Total Cut Qty"} {...props} filterKeys={filterKeys}>
      <StyledAggregateReconciliationReport />
    </Report>
  );
};
export default AggregateCuttingReport;
